import { axios, notify } from '@bento-dental/core';
import { AxiosResponse } from 'axios';
import { useMutation, UseMutationResult } from 'react-query';
import { ApiResponse, CobraBulkCancelVariables, CobraElectionVariables, CobraInfo } from './types';

type CobraElectionResponse = AxiosResponse<ApiResponse<CobraInfo>>;
type CobraBulkCancelResponse = AxiosResponse<ApiResponse<unknown>>;

const extractErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }

  return 'Something went wrong. Please try again.';
};

export const useCobra = (): {
  cobraElectionInfo: UseMutationResult<
    CobraElectionResponse,
    unknown,
    CobraElectionVariables,
    unknown
  >;
  cobraBulkCancel: UseMutationResult<
    CobraBulkCancelResponse,
    unknown,
    CobraBulkCancelVariables,
    unknown
  >;
} => {
  const cobraElectionInfo = useMutation<
    CobraElectionResponse,
    unknown,
    CobraElectionVariables,
    unknown
  >(({ patientId }) => axios.post(`corp/cobra/election-info/`, { patient: patientId }), {
    onError: (error) => {
      notify(extractErrorMessage(error), 'error');
    },
  });

  const cobraBulkCancel = useMutation<
    CobraBulkCancelResponse,
    unknown,
    CobraBulkCancelVariables,
    unknown
  >(
    ({ finalDateOfCoverage, cancelCobraIds }) =>
      axios.post(`corp/cobra/bulk-cancel/`, {
        final_date_of_coverage: finalDateOfCoverage,
        cancel_cobra_ids: cancelCobraIds,
      }),
    {
      onError: (error) => {
        notify(extractErrorMessage(error), 'error');
      },
    }
  );

  return {
    cobraElectionInfo,
    cobraBulkCancel,
  };
};
